import { createContext, useContext, useEffect, useMemo, useState } from "react";

type Mode = "light" | "dark" | "system";
type Resolved = "light" | "dark";

type Ctx = {
  mode: Mode;
  resolved: Resolved;
  setMode: (m: Mode) => void;
};

const ThemeCtx = createContext<Ctx | null>(null);

function resolve(mode: Mode): Resolved {
  if (mode === "system") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return mode;
}

function apply(r: Resolved) {
  const root = document.documentElement;
  root.classList.toggle("dark", r === "dark");
  root.style.colorScheme = r;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<Mode>(() => {
    const saved = typeof localStorage !== "undefined" ? (localStorage.getItem("theme") as Mode | null) : null;
    return saved ?? "system";
  });
  const [resolved, setResolved] = useState<Resolved>(() => resolve(mode));

  useEffect(() => {
    const r = resolve(mode);
    setResolved(r);
    apply(r);
    localStorage.setItem("theme", mode);
  }, [mode]);

  useEffect(() => {
    if (mode !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      const r = resolve("system");
      setResolved(r);
      apply(r);
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [mode]);

  const value = useMemo(() => ({ mode, resolved, setMode: setModeState }), [mode, resolved]);
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeCtx);
  if (!ctx) throw new Error("useTheme outside provider");
  return ctx;
}
